import type { DashboardSummary } from "@excellent/core/server";

import { DashboardStatGrid } from "./dashboard-stat-grid";
import { DashboardPrimaryBlock } from "./dashboard-primary-block";
import { AttentionList } from "./attention-list";
import { InsightCardList } from "./insight-card-list";
import { HeroKpi } from "./hero-kpi";
import { KpiActionList } from "./kpi-action-list";
import { ActivityGroupList } from "../activity/activity-group-list";

export type DashboardRegion =
  | "hero"
  | "stats"
  | "actions"
  | "primary"
  | "attention"
  | "insights"
  | "activity";

/**
 * Just the region fields this body renders — a structural subset of
 * {@link DashboardSummary}. Lets the generic project/workspace section
 * payloads (which omit `product`/`ctx`/`recentActivity`) render here too.
 */
export type DashboardRegionsData = Pick<
  DashboardSummary,
  | "hero"
  | "stats"
  | "actions"
  | "attention"
  | "insights"
  | "primary"
  | "recentActivityGroups"
>;

export interface DashboardRegionsProps {
  /** Pre-computed dashboard payload (region fields only). */
  summary: DashboardRegionsData;
  /** Empty-state body for the activity region. */
  activityEmptyBody?: string;
  /** Regions to hide. Use sparingly; consistency is the goal. */
  hideRegions?: DashboardRegion[];
  /**
   * Where the "Needs attention" region renders:
   *   • `"strip"` (default) — the full-width triage strip beneath the KPI grid.
   *     Legacy behavior; every existing caller keeps it.
   *   • `"rail"` — folded into the right column above activity, so attention is
   *     no longer an inline mid-page band. Used by the section overview.
   *   • `"none"` — not rendered here (the host surfaces attention elsewhere).
   * `hideRegions` containing `"attention"` still suppresses it entirely,
   * regardless of this prop.
   */
  attentionLayout?: "strip" | "rail" | "none";
}

/**
 * The five-region dashboard *body* — the headerless core shared by the
 * standalone {@link ProductDashboard}, the section product dashboards, and the
 * generic workspace/project section dashboards:
 *
 *   1. Stat grid (full width, on top)
 *   2. Attention triage strip (quiet when clear)
 *   3. Two-column body — LEFT (2fr): primary → insights; RIGHT (1fr): activity
 *
 * Was hand-assembled (the `grid-cols-[minmax(0,2fr)_minmax(0,1fr)]` + region
 * order) in every dashboard surface. Edit the layout here once.
 */
export function DashboardRegions({
  summary,
  activityEmptyBody = "No activity yet. Logged events will appear here.",
  hideRegions = [],
  attentionLayout = "strip",
}: DashboardRegionsProps) {
  const hide = new Set<DashboardRegion>(hideRegions);
  const showAttention = !hide.has("attention") && attentionLayout !== "none";
  return (
    <>
      {/* Tier 1 — the North Star hero + optimization curve. Sits above the KPI
          grid; renders nothing when a surface omits it (back-compat). See
          docs/dashboard/DASHBOARD-REDESIGN-PRD.md §2. */}
      {!hide.has("hero") && summary.hero ? (
        <HeroKpi hero={summary.hero} />
      ) : null}

      {!hide.has("stats") ? <DashboardStatGrid stats={summary.stats} /> : null}

      {/* Triage strip — actionable attention sits HIGH in the visual path,
          directly under the KPIs, not buried bottom-right. Renders nothing
          when clear (quiet over loud). See RESEARCH-CONVERGENCE-PLAN §A3 /
          PRD §4.2. The section overview opts into `attentionLayout="rail"` to
          fold this into the right column instead (no inline mid-page band). */}
      {showAttention && attentionLayout === "strip" ? (
        <AttentionList items={summary.attention} layout="strip" />
      ) : null}

      <div
        className={[
          "grid gap-8",
          "grid-cols-1",
          "@xl/page:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]",
        ].join(" ")}
      >
        <div className="flex flex-col gap-8 min-w-0">
          {/* "Move these numbers" — the KPI-anchored action layer. Leads the
              analysis column directly under the hero/KPIs, so the answer to
              "how do I improve this?" sits next to the metric it moves. Quiet
              when every KPI is on track (renders nothing). */}
          {!hide.has("actions") ? (
            <KpiActionList actions={summary.actions} />
          ) : null}
          {!hide.has("primary") ? (
            <DashboardPrimaryBlock primary={summary.primary} />
          ) : null}
          {!hide.has("insights") ? (
            <InsightCardList insights={summary.insights} />
          ) : null}
        </div>
        <div className="flex flex-col gap-8 min-w-0">
          {showAttention && attentionLayout === "rail" ? (
            <AttentionList items={summary.attention} layout="rail" />
          ) : null}
          {!hide.has("activity") ? (
            <ActivityGroupList
              groups={summary.recentActivityGroups}
              emptyBody={activityEmptyBody}
            />
          ) : null}
        </div>
      </div>
    </>
  );
}
